import { COLLECT_FIELD_TYPES, COLLECT_SAVE_TARGETS, WORKFLOW_LIMITS, type CollectFieldType, type CollectSaveTarget } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Input, Select, Textarea } from "@sagot/ui";
import { useState } from "react";
import type { CollectDataStep } from "../types";
import { isKeyedTarget, optionsText, parseOptions, sanitizeKey, saveTargetOf, saveToOf, targetKey } from "./collect-model";
import { CountedInput, CountedTextarea } from "./counted-field";
import { SwitchRow } from "./switch-row";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
/** Saving to a visitor field fixes the kind of answer asked for. */
const TARGET_FIELD_TYPE: Partial<Record<CollectSaveTarget, CollectFieldType>> = { "visitor.email": "email", "visitor.phone": "phone", "visitor.name": "text" };

/** Collect data: ask one question, check the answer, save it to the visitor, the lead or this run. */
export function CollectDataForm({ step, disabled, onChange }: FormProps<CollectDataStep>) {
  const { t } = useTranslation();
  const [choices, setChoices] = useState(() => optionsText(step.field.options));
  const save = saveTargetOf(step.save_to);
  const fixedType = TARGET_FIELD_TYPE[save.target];
  const setField = (patch: Partial<CollectDataStep["field"]>) => { onChange({ ...step, field: { ...step.field, ...patch } }); };
  const setType = (type: CollectFieldType) => { setField({ type, options: type === "dropdown" ? parseOptions(choices, step.field.options) : undefined }); };
  const setTarget = (target: CollectSaveTarget) => {
    const type = TARGET_FIELD_TYPE[target];
    const key = isKeyedTarget(target) ? save.key || sanitizeKey(step.id) : "";
    const field = type ? { ...step.field, type, options: undefined } : step.field;
    onChange({ ...step, save_to: saveToOf({ target, key }), field });
  };
  return (
    <div className="space-y-5">
      <div>
        <CountedTextarea label={t(`${F}.question`)} rows={2} value={step.text} max={WORKFLOW_LIMITS.message} disabled={disabled} onChange={(text) => { onChange({ ...step, text }); }} />
        <VariableList />
      </div>
      <CountedInput label={t(`${F}.fieldLabel`)} hint={t(`${F}.fieldLabelHint`)} value={step.field.label} max={WORKFLOW_LIMITS.fieldLabel} disabled={disabled} onChange={(label) => { setField({ label }); }} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t(`${F}.answerType`)} hint={fixedType ? t(`${F}.answerTypeFixed`) : undefined}>
          <Select value={step.field.type} disabled={disabled || fixedType !== undefined} onChange={(e) => { setType(e.target.value as CollectFieldType); }}>
            {COLLECT_FIELD_TYPES.map((type) => <option key={type} value={type}>{t(`${F}.fieldTypes.${type}`)}</option>)}
          </Select>
        </Field>
        <Field label={t(`${F}.saveTo`)}>
          <Select value={save.target} disabled={disabled} onChange={(e) => { setTarget(e.target.value as CollectSaveTarget); }}>
            {COLLECT_SAVE_TARGETS.map((target) => <option key={target} value={target}>{t(`${F}.saveTargets.${targetKey(target)}`)}</option>)}
          </Select>
        </Field>
      </div>
      {isKeyedTarget(save.target) ? (
        <Field label={t(`${F}.saveKey`)} hint={t(`${F}.saveKeyHint`)}>
          <Input className="font-mono text-xs" maxLength={WORKFLOW_LIMITS.customKey} disabled={disabled} value={save.key} onChange={(e) => { onChange({ ...step, save_to: saveToOf({ target: save.target, key: sanitizeKey(e.target.value) }) }); }} />
        </Field>
      ) : null}
      {step.field.type === "dropdown" ? (
        <Field label={t(`${F}.choices`)} hint={t(`${F}.choicesHint`, { max: WORKFLOW_LIMITS.fieldOptions })}>
          <Textarea
            rows={6}
            className="font-mono text-xs"
            disabled={disabled}
            value={choices}
            onChange={(e) => { setChoices(e.target.value); setField({ options: parseOptions(e.target.value, step.field.options) }); }}
          />
        </Field>
      ) : null}
      {save.target !== "var" ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${F}.personalData`)}</p> : null}
      <SwitchRow label={t(`${F}.required`)} description={t(`${F}.requiredHint`)} checked={step.field.required} disabled={disabled} onChange={(required) => { setField({ required }); }} />
      <SwitchRow label={t(`${F}.skipIfKnown`)} description={t(`${F}.skipIfKnownHint`)} checked={step.skip_if_known} disabled={disabled} onChange={(skip_if_known) => { onChange({ ...step, skip_if_known }); }} />
    </div>
  );
}
